import * as React from 'react'

import { connect } from 'react-redux'
import { RouteComponentProps, withRouter } from 'react-router-dom'
import { bindActionCreators, compose } from 'redux'
import { formValueSelector, InjectedFormProps, reduxForm } from 'redux-form'

import { professionValues } from '../../services/doctorProfessions'
import * as UserActions from '../../store/user/actions'

import GA from 'services/googleAnalytics'
import SnackbarService from 'services/snackbars'
import { getParamFromUrl } from 'services/utils'
import { isPasswordStrong, withLettersOnly } from 'services/validate'

import Content from './content'

import mixpanel from 'mixpanel-browser'
import './style.scss'

interface IProps
  extends InjectedFormProps,
    RouteComponentProps<{ userRole: string }> {
  signUpForm?: any
  UserLib?: any

  fullName: string
  email: string
  password: string
  confirmPassword: string
  title: string
  organization: string
  profession: string

  pName: string
  pLastName: string
  pPhone: string
}

interface IState {
  page: string
  croppedImage: any
  image: any
  loading: boolean
  formSubmitted: boolean
  agreeWithTerms: boolean
}

interface IOnetimeWarnings {
  passwordIsTooSimple: boolean
}

class SignUp extends React.Component<IProps, IState> {
  state: IState = {
    page: 'form',
    croppedImage: '',
    image: '',
    loading: false,
    formSubmitted: false,
    agreeWithTerms: false
  }

  onetimeWarnings: IOnetimeWarnings = {
    passwordIsTooSimple: false
  }

  updateOneTimeWarning = (key: keyof IOnetimeWarnings) =>
    (this.onetimeWarnings[key] = true)

  keyPressed = (e: KeyboardEvent) => {
    switch (e.keyCode) {
      case 13:
        this.enterPressed()
        break
      default:
        break
    }
  }

  enterPressed = () => this.onSubmit()

  componentDidMount() {
    const {
      history: {
        location: { state }
      },
      match: {
        params: { userRole }
      }
    } = this.props

    document.addEventListener('keydown', this.keyPressed)

    if (state && history.state.page) {
      this.setState({ page: history.state.page })
    }

    if (userRole === 'educator') {
      mixpanel.identify()
      mixpanel.track('Educator Enter Signup')
    }
  }

  componentWillUnmount() {
    document.removeEventListener('keydown', this.keyPressed)
  }

  setAgreeWithTerms = (val: boolean = true) =>
    this.setState({ agreeWithTerms: val })

  onSubmit = async () => {
    const { page, agreeWithTerms } = this.state
    const { valid, signUpForm } = this.props

    this.setState({
      formSubmitted: true
    })

    if (!valid) {
      SnackbarService.showSnackbar('Please enter valid data!', 'error')
      return
    }

    if (!withLettersOnly(signUpForm.pName)) {
      SnackbarService.showSnackbar(
        'Only letters are allowed in a first name field',
        'error'
      )
      return
    }

    if (!withLettersOnly(signUpForm.pLastName, true)) {
      SnackbarService.showSnackbar(
        'Only letters and spaces are allowed in a last name field',
        'error'
      )
      return
    }

    if (
      !isPasswordStrong(signUpForm.password) &&
      !this.onetimeWarnings.passwordIsTooSimple
    ) {
      SnackbarService.showSnackbar(
        'We suggest a strong password with at least 1 lowercase letter, 1 uppercase letter, and 1 digit',
        'info'
      )
      this.updateOneTimeWarning('passwordIsTooSimple')
      return
    }

    if (!agreeWithTerms) {
      SnackbarService.showSnackbar(
        'Please accept our Privacy Policy and Terms of Use',
        'error'
      )
      return
    }

    if (page === 'form') {
      this.doSignUp(false)
      return
    }
  }

  handleFiles = ({ base64 }) => {
    const { page } = this.state
    if (page === 'add_photo' && base64.includes('image')) {
      this.setState({ image: base64, page: 'crop-avatar' })
    }
  }

  doSignUp = async (withImage: boolean = true) => {
    const {
      valid,
      signUpForm,
      match: {
        params: { userRole }
      }
    } = this.props
    const { croppedImage: image } = this.state

    let signUpData: any = null

    if (userRole === 'parent' || userRole === 'educator') {
      signUpData = {
        ...signUpForm,
        full_name: `${signUpForm.pName} ${signUpForm.pLastName}`
      }
    }

    if (!signUpData) {
      SnackbarService.showSnackbar('Error occured!', 'error')
      return
    } else if (!valid) {
      SnackbarService.showSnackbar('Please, enter valid data!', 'error')
      return
    } else if (withImage && !image) {
      SnackbarService.showSnackbar('Please, add a profile picture!', 'error')
      return
    }

    this.setState({ loading: true })
    if (withImage) {
      this.props.UserLib.uploadImage(
        await (await fetch(image)).blob(),
        null,
        url => {
          this.doSignUpAsync(userRole, signUpData, url)
        },
        errorMessage => {
          SnackbarService.showSnackbar(errorMessage, 'error')
          this.setState({ loading: false })
        }
      )
    } else {
      this.doSignUpAsync(userRole, signUpData)
    }
  }

  goBackAction = () => {
    const { history } = this.props
    const { page } = this.state
    if (page === 'add_photo') {
      this.setState({ page: 'form' })
    } else {
      history.goBack()
    }
  }

  onChangeSelect = (name: string, value: string) => {
    this.props.change(name, value)
  }

  onChangeMaskedInput = (name: string, value: string) => {
    this.props.change(name, value)
  }

  render() {
    const {
      page,
      croppedImage,
      loading,
      formSubmitted,
      agreeWithTerms
    } = this.state
    const {
      valid,
      anyTouched,
      match: {
        params: { userRole }
      }
    } = this.props

    return (
      <>
        <Content
          doSignUp={this.doSignUp}
          professionValues={professionValues}
          onChangeSelect={this.onChangeSelect}
          isValidForm={valid}
          isAnyTouched={anyTouched}
          formSubmitted={formSubmitted}
          userRole={userRole}
          onChangeMaskedInput={this.onChangeMaskedInput}
          page={page}
          goBackAction={this.goBackAction}
          onSubmit={this.onSubmit}
          image={croppedImage}
          handleFiles={this.handleFiles}
          loading={loading}
          agreeWithTerms={agreeWithTerms}
          setAgreeWithTerms={this.setAgreeWithTerms}
        />
      </>
    )
  }

  private doSignUpAsync = (
    userRole: string,
    signUpData: any,
    url: string = ''
  ) => {
    signUpData.email = signUpData.email.toLowerCase()
    const { UserLib } = this.props
    UserLib.signUp(
      userRole,
      {
        ...signUpData,
        avatar_url: url
      },
      async () => {
        // google tag manager push for successful sign up
        GA.push({
          event: 'formSubmission',
          formType: 'SignUp'
        })
        // this.setState({
        //   page: 'registered',
        //   loading: false
        // })
        // UserLib.setIsGetPhoneNumber(true)
        // commented this out because the route is already added to the stack
        // history.push('/payment')
      },
      (errorMessage: string) => {
        SnackbarService.showSnackbar(errorMessage, 'error')
        this.setState({ loading: false })
      }
    )
  }
}

const SignUpContainer = reduxForm({
  form: 'signup'
})(SignUp)

const selector = formValueSelector('signup')

const mapStateToProps = (state, ownProps) => {
  const signUpForm = {
    pName: (selector(state, 'pName') || '').trim(),
    pLastName: (selector(state, 'pLastName') || '').trim(),
    pPhone: selector(state, 'pPhone') || '',
    email: (selector(state, 'email') || '').trim(),
    password: selector(state, 'password') || ''
  }

  const {
    location: { search },
    history: {
      location: { state: historyState }
    }
  } = ownProps
  const name = decodeURIComponent(getParamFromUrl(search, 'first_name')).split(
    '+'
  )
  const lastName = decodeURIComponent(
    getParamFromUrl(search, 'last_name')
  ).split('+')
  const pName = name[0]
  const pLastName = lastName[0]

  const email = decodeURIComponent(getParamFromUrl(search, 'email'))
  if (decodeURIComponent(getParamFromUrl(search, 'password'))) {
    SnackbarService.showSnackbar(
      'Sorry, something went wrong (Error 10), Please contact support@giantleap.tech',
      'error'
    )
    setTimeout(() => (window.location.href = '/signup/parent'), 1000)
  }
  const initialValues = {
    email: (historyState && historyState.email) || email,
    pName: (historyState && historyState.name) || pName,
    pLastName: (historyState && historyState.lastName) || pLastName,
    password: historyState && historyState.password
  }

  return {
    ...signUpForm,
    signUpForm,
    initialValues
  }
}

const mapDispatchToProps = dispatch => {
  return {
    UserLib: bindActionCreators<any, any>(UserActions as any, dispatch)
  }
}

const connector = connect(mapStateToProps, mapDispatchToProps)

export default compose(withRouter, connector)(SignUpContainer)



// WEBPACK FOOTER //
// ./src/pages/SignUp/index.tsx